/**
 * Browser-side web-preview generation.
 *
 * The original file is uploaded untouched; this produces a SEPARATE compressed
 * JPEG used only for grid and lightbox rendering. Nothing here ever affects
 * what a client downloads.
 *
 * Doing it in the browser (rather than with sharp on the server) means the
 * server never has to stream 20GB of originals back out of R2 to resize them,
 * needs no job queue, and no image-processing native dependency. The cost is
 * that quality depends on the browser's resampler - mitigated below by
 * downscaling in halving steps, which avoids the aliasing you get from one
 * giant single-pass drawImage.
 */

/**
 * Two tiers, sized against 3000px-long-edge JPEG originals.
 *
 *   THUMB 900px / q0.72 (~90KB) - the grid tile. The grid shows every photo at
 *     once, so this number is the one that decides whether a 400-photo gallery
 *     is pleasant or painful on a phone: ~36MB of scrolling instead of ~1.6GB
 *     if the grid used the originals. 900px covers the largest tile the layout
 *     produces (320 CSS px at 2x DPR = 640) with headroom for 3x phones.
 *
 *   PREVIEW 1800px / q0.82 (~450KB) - the lightbox copy. Full-screen on a
 *     1440px-wide laptop at 2x, or any phone, without pulling the 3000px file.
 *
 * At 3000px sources the derivatives together add roughly 15% to stored bytes -
 * cheap on R2, and it means the client never waits on a full-size image to
 * browse. The original is untouched and remains the only thing that downloads.
 */
const TIERS = {
  thumb: { maxEdge: 900, quality: 0.72 },
  preview: { maxEdge: 1800, quality: 0.82 },
};

export async function makePhotoDerivatives(file) {
  const bitmap = await loadBitmap(file);
  if (!bitmap) return null;

  const out = { width: bitmap.width, height: bitmap.height };
  for (const [name, tier] of Object.entries(TIERS)) {
    out[name] = await resizeToBlob(bitmap, tier);
  }
  if (bitmap.close) bitmap.close();
  return out;
}

async function resizeToBlob(bitmap, { maxEdge, quality }) {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const targetW = Math.max(1, Math.round(bitmap.width * scale));
  const targetH = Math.max(1, Math.round(bitmap.height * scale));

  // Halve repeatedly until within 2x of the target, then take the final step.
  // A single big drawImage aliases badly; stepping down does not. This matters
  // most for the 900px thumb, which is a 3.3x reduction from a 3000px source.
  let canvas = drawTo(bitmap, bitmap.width, bitmap.height);
  let w = bitmap.width;
  let h = bitmap.height;
  while (w / 2 > targetW && h / 2 > targetH) {
    w = Math.round(w / 2);
    h = Math.round(h / 2);
    canvas = drawTo(canvas, w, h);
  }
  canvas = drawTo(canvas, targetW, targetH);
  return toBlob(canvas, quality);
}

function drawTo(source, w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

function toBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

async function loadBitmap(file) {
  try {
    // imageOrientation honours EXIF rotation, which phone-shot JPEGs rely on.
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null; // e.g. a RAW file the browser can't decode - we upload the original and skip the preview
  }
}

/** Reads intrinsic dimensions/duration so the grid can reserve space before load. */
export function probeVideo(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = (meta) => {
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      done({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
    video.onerror = () => done({});
    video.src = url;
  });
}

/** First-frame poster for the video card, so the section isn't a black box. */
export function grabVideoPoster(file, atSeconds = 1) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.onseeked = () => {
      // 1080p source, so the poster is downscaled to 1280 wide - it is a still
      // shown before playback, not something anyone inspects closely.
      const scale = Math.min(1, 1280 / video.videoWidth);
      const canvas = drawTo(video, Math.round(video.videoWidth * scale), Math.round(video.videoHeight * scale));
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        },
        'image/jpeg',
        0.8,
      );
    };
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(atSeconds, (video.duration || 2) / 2);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    video.src = url;
  });
}
